import type { ReactNode } from 'react';
import { useT } from '../../i18n';
import { makeStyles, tokens } from '@fluentui/react-components';

export interface DataTableColumn<T> {
  key: string;
  header: string;
  align?: 'left' | 'right' | 'center';
  /** Ancho relativo tipo grid-template-columns fraction, ej. 1.6 → "1.6fr" */
  width?: number;
  render?: (row: T) => ReactNode;
}

interface Props<T> {
  columns: DataTableColumn<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  /** Fila de totales/promedio opcional, mismas columnas que `columns` */
  totals?: Partial<Record<string, ReactNode>>;
  totalsLabel?: string;
  isLoading?: boolean;
  emptyMessage?: string;
  /** Si se pasa, cada fila es clicable (y alcanzable con teclado: Enter/Espacio). */
  onRowClick?: (row: T) => void;
  /** Resalta la fila seleccionada (listas maestro-detalle). */
  isRowSelected?: (row: T) => boolean;
}

const useStyles = makeStyles({
  wrap: { display: 'flex', flexDirection: 'column', fontSize: '13px' },
  headerRow: {
    color: tokens.colorNeutralForeground3,
    fontWeight: 600,
    textAlign: 'left',
    padding: '0 8px 6px',
  },
  row: {
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    padding: '8px',
  },
  totalsRow: {
    borderTop: `2px solid ${tokens.colorNeutralStroke1}`,
    fontWeight: 700,
    padding: '8px',
  },
  selected: { backgroundColor: tokens.colorBrandBackground2 },
  clickable: { cursor: 'pointer', ':hover': { backgroundColor: tokens.colorNeutralBackground1Hover } },
  empty: {
    padding: '16px 8px',
    color: tokens.colorNeutralForeground3,
    textAlign: 'center',
  },
});

function gridTemplate<T>(columns: DataTableColumn<T>[]) {
  return columns.map(c => (c.width ? `${c.width}fr` : '1fr')).join(' ');
}

/** Tabla genérica de columnas tipadas con fila de totales opcional — reemplaza tablas ad hoc por página. */
export function DataTable<T>({ columns, rows, rowKey, totals, totalsLabel, isLoading, emptyMessage, onRowClick, isRowSelected }: Props<T>) {
  const s = useStyles();
  const t = useT();
  const gridTemplateColumns = gridTemplate(columns);

  if (isLoading) return <div className={s.empty}>…</div>;
  if (!rows.length) return <div className={s.empty}>{emptyMessage ?? t.common.noData}</div>;

  return (
    <div className={s.wrap}>
      <div className={s.headerRow} style={{ display: 'grid', gridTemplateColumns }}>
        {columns.map(c => (
          <span key={c.key} style={{ textAlign: c.align ?? 'left' }}>{c.header}</span>
        ))}
      </div>

      {rows.map(row => (
        <div
          key={rowKey(row)}
          className={[s.row, onRowClick ? s.clickable : '', isRowSelected?.(row) ? s.selected : ''].filter(Boolean).join(' ')}
          style={{ display: 'grid', gridTemplateColumns }}
          {...(onRowClick ? {
            role: 'button', tabIndex: 0,
            onClick: () => onRowClick(row),
            onKeyDown: (e: React.KeyboardEvent) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onRowClick(row); } },
          } : {})}
        >
          {columns.map(c => (
            <span key={c.key} style={{ textAlign: c.align ?? 'left' }}>
              {c.render ? c.render(row) : String((row as Record<string, unknown>)[c.key] ?? '')}
            </span>
          ))}
        </div>
      ))}

      {totals && (
        <div className={s.totalsRow} style={{ display: 'grid', gridTemplateColumns }}>
          <span>{totalsLabel ?? t.common.total}</span>
          {columns.slice(1).map(c => (
            <span key={c.key} style={{ textAlign: c.align ?? 'left' }}>{totals[c.key] ?? ''}</span>
          ))}
        </div>
      )}
    </div>
  );
}
